import { type Interval } from 'date-fns';

import { type AkaVeranstaltungId, type Standort } from '../../../../../dtos';
import {
  type DateInterval,
  getIntervalAlignedToStartAndEndOfDay,
  mergeConsecutiveIntervals,
} from '../../../../../utils/dateAndIntervalHelpers.js';
import { type CalendarEvent, CalendarStandortEvent } from '../CalendarEvent';
import { calendarResourceTypes } from '../calendarResourceTypes';
import { AbstractCalendarResource } from './AbstractCalendarResource';
import {
  eventCreationStrategies,
  type EventCreationStrategy,
  type IntervalWithOptionalVeranstaltung,
} from './AbstractCalendarResource.types.js';

export class CalendarStandortResource extends AbstractCalendarResource {
  public readonly type = calendarResourceTypes.STANDORT;

  public readonly id: `s-${string}`;

  public readonly name: string;

  public readonly standort: Standort;

  public readonly eventCreationStrategy: EventCreationStrategy =
    eventCreationStrategies.SINGLE_EVENT_SPANNING_ALL_ABLAUFTAGE;

  public constructor(standort: Standort) {
    super();
    this.standort = standort;
    this.id = `s-${standort.standortSapId}`;
    this.name = `${standort.kuerzel} / ${standort.name}`;
  }

  public createFixedEventsFromBlockungen(akaVeranstaltungId: AkaVeranstaltungId | null): CalendarEvent[] {
    const events: CalendarStandortEvent[] = [];

    for (const blockung of this.standort.standortBlockungen) {
      for (const interval of this.mergeConsecutiveIntervalsAndAlignToDayBounds(blockung.zeitraeume)) {
        const newEvent = this.createEventFromBlockungAndInterval(blockung, interval);
        newEvent.isOldSelection = blockung.veranstaltung.akaVeranstaltungId === akaVeranstaltungId;

        events.push(newEvent);
      }
    }

    return events;
  }

  public createPendingEventsFromBlockungen(akaVeranstaltungId: AkaVeranstaltungId): CalendarEvent[] {
    const events: CalendarStandortEvent[] = [];

    for (const blockung of this.standort.standortBlockungen.filter(
      (_blockung) => _blockung.veranstaltung.akaVeranstaltungId === akaVeranstaltungId,
    )) {
      for (const interval of mergeConsecutiveIntervals(blockung.zeitraeume)) {
        const newEvent = this.createEventFromBlockungAndInterval(blockung, interval);
        newEvent.isCurrentSelection = true;
        newEvent.resize = false;
        newEvent.editable = true;

        events.push(newEvent);
      }
    }

    return events;
  }

  public createPendingEventsFromSuggestions() {
    return [];
  }

  private mergeConsecutiveIntervalsAndAlignToDayBounds(intervals: DateInterval[]): DateInterval[] {
    return mergeConsecutiveIntervals(intervals).map(getIntervalAlignedToStartAndEndOfDay);
  }

  private createEventFromBlockungAndInterval(
    blockung: (typeof this.standort.standortBlockungen)[number],
    interval: Interval<Date, Date>,
  ) {
    return new CalendarStandortEvent(interval.start, interval.end, this, {
      locationKuerzel: this.standort.kuerzel,
      quelleTerminId: blockung.veranstaltung.akaVeranstaltungId,
      veranstaltung: blockung.veranstaltung,
      zeitraeume: blockung.zeitraeume,
      gruppenraumNotwendig: blockung.veranstaltung.thema.planungsinformation?.gruppenraumNotwendig ?? null,
      raumgroesse: blockung.veranstaltung.thema.planungsinformation?.raumgroesse,
    });
  }

  public createNewCalendarEventByInterval(interval: Interval<Date, Date>): CalendarStandortEvent {
    const event = new CalendarStandortEvent(interval.start, interval.end, this, {
      // unimportant values
      locationKuerzel: null,
      quelleTerminId: '',
      veranstaltung: null,
      gruppenraumNotwendig: [],

      // important values
      zeitraeume: this.createZeitraeumeByInterval(interval),
    });
    event.isCurrentSelection = true;

    return event;
  }

  public getRessourceSapId(): number {
    return this.standort.standortSapId;
  }

  public isBookableAt(): boolean {
    return true;
  }

  protected getBlockungen(): IntervalWithOptionalVeranstaltung[] {
    return this.standort.standortBlockungen;
  }
}
